import { isNumber } from 'lodash';
import { Result, err, ok } from 'neverthrow';
import { StreamParser } from '@json2csv/plainjs';
import React from 'react';

export function classNames(
  ...classes: (string | undefined | boolean)[]
): string {
  return classes.filter(Boolean).join(' ');
}

export function getCurrencySymbol(
  currency: string
): Result<string, 'InvalidCurrency'> {
  if (!ISO_4217_CURRENCY_CODES.includes(currency.toUpperCase() as any)) {
    return err('InvalidCurrency');
  }

  const locale =
    navigator.languages !== undefined && navigator.languages.length
      ? navigator.languages[0]
      : navigator.language;

  try {
    return ok(
      (0)
        .toLocaleString(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim()
    );
  } catch (e) {
    if (e instanceof Error && e.message.includes('Invalid currency code')) {
      return err('InvalidCurrency');
    }

    throw e;
  }
}

export const ISO_4217_CURRENCY_CODES = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BOV',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHE',
  'CHF',
  'CHW',
  'CLF',
  'CLP',
  'COP',
  'COU',
  'CRC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LYD',
  'MAD',
  'MDL',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRU',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MXV',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'CNY',
  'RUB',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLE',
  'SLL',
  'SOS',
  'SRD',
  'SSP',
  'STN',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'USN',
  'UYI',
  'UYU',
  'UYW',
  'UZS',
  'VED',
  'VES',
  'VND',
  'VUV',
  'WST',
  'XAF',
  'XAG',
  'XAU',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XCD',
  'XDR',
  'XOF',
  'XPD',
  'XPF',
  'XPT',
  'XSU',
  'XTS',
  'XUA',
  'XXX',
  'YER',
  'ZAR',
  'ZMW',
  'ZWL',
];

export const jsonToCsv = async (json: any[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    const opts = {};
    const asyncOpts = {};
    const parser = new StreamParser(opts, asyncOpts);

    if (!json.length) {
      return resolve('');
    }

    let csv = '';
    parser.onData = (chunk) => (csv += chunk.toString());
    parser.onEnd = () => resolve(csv);
    parser.onError = (err) => reject(err);

    json.forEach((row) => parser.pushLine(row));
    parser.end();
  });
};

/**
 * Serialises rows to CSV a batch at a time, so the whole row set is never held
 * in JS at once — the counterpart to {@link jsonToCsv} for a producer that pages
 * its rows rather than handing them over whole.
 *
 * One `StreamParser` spans every batch, so the header is written once (from the
 * first row's fields) and the rows that follow are body only. The output comes
 * back as the array of chunks the parser emitted, ready for a `Blob` — which is
 * backed outside the JS heap — rather than concatenated into one growing string.
 */
export const jsonBatchesToCsv = async (
  batches: AsyncIterable<Record<string, unknown>[]>
): Promise<{ chunks: string[]; rowCount: number }> => {
  const parser = new StreamParser({}, {});
  const chunks: string[] = [];
  let rowCount = 0;

  const settled = new Promise<void>((resolve, reject) => {
    parser.onData = (chunk) => chunks.push(chunk.toString());
    parser.onEnd = () => resolve();
    parser.onError = (err) => reject(err);
  });

  for await (const batch of batches) {
    for (const row of batch) {
      parser.pushLine(row);
      rowCount += 1;
    }
  }

  // No rows means no header either — an empty export is empty, matching
  // `jsonToCsv([])`, and lets a caller tell "nothing to export" from a file.
  // `end()` is skipped rather than caught: the parser throws on a stream it was
  // given no data and no explicit fields, and there is nothing to flush anyway.
  if (rowCount === 0) {
    return { chunks: [], rowCount: 0 };
  }
  parser.end();
  await settled;

  return { chunks, rowCount };
};

export const downloadFile = (
  contents: string[],
  type: 'csv' | 'json',
  name?: string
) => {
  const file = new Blob(contents, { type: `text/${type}` });
  const element = document.createElement('a');
  element.href = URL.createObjectURL(file);
  element.download = `${name ?? Date.now()}.${type}`;
  document.body.appendChild(element);
  element.click();
};

const toUtc = (
  date: Date,
  opts?: {
    hours?: boolean;
    minutes?: boolean;
    seconds?: boolean;
    milliseconds?: boolean;
  }
) => {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    opts?.hours ? date.getUTCHours() : 0,
    opts?.minutes ? date.getUTCMinutes() : 0,
    opts?.seconds ? date.getUTCSeconds() : 0,
    opts?.milliseconds ? date.getUTCMilliseconds() : 0
  );
};

export class UTCDate extends Date {
  constructor(
    date?: Date | string | number | null | undefined,
    opts?: {
      hours?: boolean;
      minutes?: boolean;
      seconds?: boolean;
      milliseconds?: boolean;
    }
  ) {
    if (date === null || date === undefined) {
      super();
      return;
    }
    if (date instanceof Date) {
      super(toUtc(date, opts));
      return;
    }
    if (typeof date === 'string') {
      if (!isNaN(Number(date))) {
        super(toUtc(new Date(Number(date)), opts));
        return;
      }
      super(toUtc(new Date(Date.parse(date)), opts));
      return;
    }
    if (isNumber(date)) {
      super(toUtc(new Date(Number(date)), opts));
      return;
    }
  }
}

export const joinElements = (
  elements: React.ReactNode[],
  joiner: (i: number) => React.ReactNode
) => {
  return elements
    .reduce<React.ReactNode[]>((acc, el, i) => {
      if (i === 0) {
        return [el];
      }

      return [...acc, joiner(i), el];
    }, [] as React.ReactNode[])
    .map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>);
};
